import { Accommodation, Instruction } from "../../app/pages/schedule-trip/schedule-trip.constants";
import { ServiceType } from "src/services/subscription/plan-service-type.enum";
import { BaseLocation } from "src/services/subscription/base-locations.enum";
import {
  OldOrderTime,
  OrderTime,
} from "../../app/redesign/components/form-field/order-time-form-field/order-time.enum";
import { OrderStatus } from "src/services/order/order-status.type";
import { camelCaseToDisplayString } from "src/services/utils/string.utils";
import { createEnumObject } from "src/services/utils/type.utils";
import { BackendBody } from "../../shared/backend-api.types";

export type FirstTimePickupStep = NonNullable<BackendBody["ApiOrder"]["nextFirstTimePickupStep"]>;
export type LocalDeliveryStep = NonNullable<BackendBody["ApiOrder"]["nextLocalDeliveryStep"]>;
export type LocalPickupStep = NonNullable<BackendBody["ApiOrder"]["nextLocalPickupStep"]>;

export const FirstTimePickupStepValues = createEnumObject<FirstTimePickupStep>({
  reviewOrder: "reviewOrder",
  assignDriver: "assignDriver",
  acceptOrRejectOrder: "acceptOrRejectOrder",
  schedulePickup: "schedulePickup",
  startPickup: "startPickup",
  completePickup: "completePickup",
  dropGearToWarehouseManager: "dropGearToWarehouseManager",
  onboardGear: "onboardGear",
  completeOnBoarding: "completeOnBoarding",
  completed: "completed",
});

export const LocalDeliveryStepValues = createEnumObject<LocalDeliveryStep>({
  reviewOrder: "reviewOrder",
  putItemInStagingZone: "putItemInStagingZone",
  assignDriver: "assignDriver",
  acceptOrRejectOrder: "acceptOrRejectOrder",
  startPickupInWarehouse: "startPickupInWarehouse",
  completePickupInWarehouse: "completePickupInWarehouse",
  startDelivery: "startDelivery",
  completeDelivery: "completeDelivery",
  completed: "completed",
});

export const LocalPickupStepValues = createEnumObject<LocalPickupStep>({
  reviewOrder: "reviewOrder",
  assignDriver: "assignDriver",
  acceptOrRejectOrder: "acceptOrRejectOrder",
  schedulePickup: "schedulePickup",
  startPickup: "startPickup",
  completePickup: "completePickup",
  dropGearToWarehouseManager: "dropGearToWarehouseManager",
  storeGear: "storeGear",
  completeOnBoarding: "completeOnBoarding",
  completed: "completed",
});

export type OrderStepForAnyServiceType = FirstTimePickupStep | LocalDeliveryStep | LocalPickupStep;

export function orderStepToString(step: OrderStepForAnyServiceType | undefined): string {
  if (!step) {
    return "No step";
  }

  return camelCaseToDisplayString(step);
}

export interface Preferences {
  accommodation?: Accommodation;
  details?: string;
  hotelName?: string;
  instruction?: Instruction;
  reservationDetails?: string;
  unitNumber?: string;
}

export interface OrderAddress {
  apartment?: string;
  country: string;
  formatted_address: string;
  id?: string;
  lat: string | number;
  lng: string | number;
  name?: string;
  placeId: string;
  state: string;
  street: string;
  zipCode: string;
}

export interface Order {
  address: OrderAddress;
  baseLocation?: BaseLocation;
  contactName?: string;
  cost?: number;
  createdAt: Date | string;
  deleteRequested?: boolean;
  destinationConcierge?: string;
  driverAccepted?: boolean;
  driverId?: string | null;
  fulfillmentDate: string;
  fulfillmentTime: OrderTime | OldOrderTime;
  id: string;
  items?: string[];
  month?: number;
  nextFirstTimePickupStep?: FirstTimePickupStep;
  nextLocalDeliveryStep?: LocalDeliveryStep;
  nextLocalPickupStep?: LocalPickupStep;
  overseer?: string;
  phoneNumber?: string;
  preferences?: Preferences;
  price: number;
  serviceType: ServiceType;
  status: OrderStatus;
  subscriptionId: string;
  tripId?: string;
  type?: string;
  updatedAt?: Date;
  userId: string;
  year?: number;
}

export interface FullTripOrderIds {
  deliveryOrderId?: string;
  pickupOrderId: string;
}

export type NewOrder = Omit<Order, "id">;

export interface FullTripOrders {
  deliveryOrder: NewOrder;
  pickupOrder: NewOrder;
}
